import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import { DragOverlay, useDndContext } from '@wix/patterns-dnd-kit-core';
import { useDragAndDropContext } from './DragAndDropContext';
import { CSS, Transform } from '@wix/patterns-dnd-kit-utilities';
import { restrictContainerToWindowEdges } from './restrictContainerToWindowEdges';
import { restrictKeyboardToVerticalAxis } from './restrictKeyboardToVerticalAxis';
import { useDefaultDragOverlayProps } from './useDefaultDragOverlayProps';

export function TableRowHorizontalScrollDragOverlay({
  children,
}: {
  children?: ReactNode;
}) {
  const {
    dropAnimation: dropAnimationProp,
    DragOverlayComponentType = DragOverlay,
  } = useDragAndDropContext();
  const { scrollableAncestorRects, activeNodeRect } = useDndContext();

  const containerNodeRect = scrollableAncestorRects[0];

  const containerWidth = containerNodeRect?.width;
  const widthRef = useRef(containerWidth);
  if (containerWidth != null) {
    widthRef.current = containerWidth;
  }

  const containerLeft = containerNodeRect?.left;
  const leftRef = useRef(containerLeft);
  if (containerNodeRect != null) {
    leftRef.current = containerLeft;
  }

  const style = useMemo(
    () => ({
      left: leftRef.current,
      width:
        widthRef.current != null && activeNodeRect?.width != null
          ? Math.min(widthRef.current, activeNodeRect.width)
          : widthRef.current,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widthRef.current, leftRef.current, activeNodeRect],
  );

  const keyframes = useCallback(
    ({
      transform: { initial, final },
    }: {
      transform: {
        initial: Transform;
        final: Transform;
      };
    }) => {
      return [
        {
          transform: CSS.Transform.toString(initial),
        },
        {
          transform: CSS.Transform.toString({
            ...final,
            x: initial.x,
          }),
        },
      ];
    },
    [],
  );

  const dropAnimation = useMemo(
    () => ({
      ...dropAnimationProp,
      keyframes,
    }),
    [dropAnimationProp, keyframes],
  );

  const defaultDragOverlayProps = useDefaultDragOverlayProps();

  return (
    <DragOverlayComponentType
      {...defaultDragOverlayProps}
      dropAnimation={dropAnimation}
      modifiers={[
        restrictContainerToWindowEdges,
        restrictKeyboardToVerticalAxis,
      ]}
      style={{
        ...defaultDragOverlayProps.style,
        ...style,
      }}
    >
      {children}
    </DragOverlayComponentType>
  );
}
